import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, RefreshCw, ArrowLeft, Mail, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function GroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const reload = async () => {
    try {
      const r = await api.get(`/groups/${id}`);
      setGroup(r.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't load this group."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authLoading]);

  // Side-effect redirect for anonymous visitors — must NOT happen during
  // render (causes a blank flash). Wrapped in a useEffect so React fires
  // it after the render cycle completes.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?next=/groups/${id}`, { replace: true });
    }
  }, [authLoading, user, id, navigate]);

  if (authLoading || !user || loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="group-detail-loading">
        <Loader2 size={28} className="animate-spin text-[var(--gold)] mx-auto" />
        <div className="overline mt-5">Loading group…</div>
      </div>
    );
  }
  if (!group) return null;

  const isOwner = user && group.owner_user_id === user.id;

  const resend = async (email) => {
    setResending(email);
    try {
      await api.post(`/groups/${id}/resend/${encodeURIComponent(email)}`);
      toast.success(`Invitation resent to ${email}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend the invitation.");
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="group-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--emerald)] inline-flex items-center gap-2"
        data-testid="group-detail-back-btn"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back
      </button>

      <div className="mt-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2">
            <Users size={14} strokeWidth={1.5} className="text-[var(--gold)]" />
            {isOwner ? "Group you're hosting" : "Group you've joined"}
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-2"
            data-testid="group-detail-name"
          >
            {group.name}
          </h1>
          {group.destination && (
            <p className="text-sm uppercase tracking-[0.18em] text-[var(--brand-red)] mt-2">
              {group.destination}
              {group.travel_window ? ` · ${group.travel_window}` : ""}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="overline">Accepted</div>
          <div className="font-display text-3xl text-[var(--emerald)]" data-testid="group-detail-accepted-count">
            {group.accepted_count}/{group.invite_count}
          </div>
        </div>
      </div>

      {group.notes && (
        <div className="mt-6 lux-card !p-5 text-sm text-[var(--ink-2)] leading-relaxed">
          {group.notes}
        </div>
      )}

      <div className="mt-10">
        <div className="overline mb-3">Travel companions</div>
        <div className="border-t border-[var(--emerald)]/15">
          {group.members.map((m) => (
            <div
              key={m.email}
              className="grid grid-cols-[1fr_auto] gap-4 py-4 border-b border-[var(--emerald)]/10 items-center"
              data-testid={`group-detail-member-${m.email}`}
            >
              <div>
                <div className="text-sm text-[var(--ink)]">{m.name}</div>
                <div className="text-xs text-[var(--ink-2)] mt-0.5">
                  {m.email}
                  {m.phone ? ` · ${m.phone}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {m.status === "accepted" ? (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--emerald)]">
                    <CheckCircle2 size={12} strokeWidth={2} /> Accepted
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)]">
                    <Clock size={12} strokeWidth={2} /> Awaiting
                  </span>
                )}
                {isOwner && m.status !== "accepted" && (
                  <button
                    onClick={() => resend(m.email)}
                    disabled={resending === m.email}
                    className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid={`group-detail-resend-${m.email}`}
                  >
                    <RefreshCw size={11} strokeWidth={1.5} />
                    {resending === m.email ? "Sending…" : "Resend"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <button
          onClick={() => navigate("/")}
          className="btn-primary"
          data-testid="group-detail-talk-sasha-btn"
        >
          <Mail size={14} strokeWidth={1.5} /> Plan the trip with Sasha
        </button>
        <button
          onClick={() => navigate("/portal")}
          className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] px-3 py-2 self-center"
          data-testid="group-detail-portal-btn"
        >
          Back to portal
        </button>
      </div>
    </div>
  );
}
